import { useAuth } from '@/hooks/useAuth';
import type { ChannelPreviewData } from './types';

/**
 * Who the preview "speaks as" when no real channel account is known:
 * the current BRAND (team name + logo/initials). Previews must never show a
 * hardcoded product/account identity.
 */
export interface PreviewIdentity {
  name: string;
  handle: string;
  initials: string;
  avatarUrl?: string;
}

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('') || '?';

export const handleOf = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, '');

export const buildPreviewIdentity = (name: string, logoUrl?: string | null): PreviewIdentity => ({
  name,
  handle: handleOf(name),
  initials: initialsOf(name),
  avatarUrl: logoUrl ?? undefined,
});

/** The signed-in user's effective brand (God Mode team first, else home team). */
export function useBrandPreviewIdentity(): PreviewIdentity {
  const { user } = useAuth();
  const team = user?.currentTeam ?? user?.team ?? null;
  return buildPreviewIdentity(team?.name ?? '', team?.logo);
}

/** A channel entry for a platform that speaks as the given identity. */
export const identityChannel = (
  platform: string,
  identity: PreviewIdentity
): ChannelPreviewData => ({
  platform,
  displayName: identity.name,
  handle: identity.handle,
  avatarUrl: identity.avatarUrl,
  initials: identity.initials,
});
